import {
  AbsolutePath,
  CancellablePromise,
  FormManager,
  Path,
  SealedFormManagerEvent,
} from "@kform/core";
import * as React from "react";

import { ignorePromiseCancellationException } from "../utils/ignorePromiseCancellationException";
import { useLatestValues } from "../utils/useLatestValues";
import { useFormManager } from "./useFormManager";
import { useResolvedPath } from "./useResolvedPath";

/**
 * Options available to the {@link useSubscription} hook.
 */
export interface SubscriptionOptions {
  /**
   * Required if no form context is in scope.
   *
   * If a form context is in scope and this value is also provided, then the
   * provided form manager will be used, in which case the current path of the
   * form context is ignored.
   */
  formManager?: FormManager;
  /**
   * Function called after the subscription completes, but before any event is
   * emitted.
   */
  onSubscribe?: () => void | PromiseLike<void>;
  /**
   * Function called after unsubscribing.
   */
  onUnsubscribe?: () => void;
  /**
   * Whether to enable the subscription.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook that subscribes to all form manager events with paths matching
 * {@link path} by running {@link onFormManagerEvent} for each event.
 *
 * Changes to {@link onFormManagerEvent}, {@link onSubscribe}, or
 * {@link onUnsubscribe} do not cause new subscriptions to be performed.
 * @param path Path of events to subscribe to.
 * @param onFormManagerEvent Function called for each event with a path matching
 * {@link path}.
 * @param subscriptionOptions Available options.
 */
export function useSubscription<T = unknown, TChildren = unknown>(
  path: Path | string = Path.CURRENT,
  onFormManagerEvent: (
    event: SealedFormManagerEvent<T, TChildren>,
  ) => void | PromiseLike<void>,
  {
    formManager: formManagerOption,
    onSubscribe,
    onUnsubscribe,
    enabled = true,
  }: SubscriptionOptions = {},
): void {
  const formManager = useFormManager(formManagerOption);
  const absolutePath = useResolvedPath(
    path,
    formManagerOption != null ? AbsolutePath.ROOT : undefined,
  );

  // Store listeners in a stable identity object since we don't want to
  // re-subscribe whenever they change
  const listeners = useLatestValues({
    onFormManagerEvent,
    onSubscribe,
    onUnsubscribe,
  });

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    let cleanedUp = false;
    let unsubscribe: (() => CancellablePromise<void>) | undefined = undefined;
    const subscriptionPromise = formManager
      .subscribe<T, TChildren>(
        absolutePath,
        (event) => {
          // Unsubscribing is asynchronous, so it's possible that an event is
          // emitted after cleaning up, but before unsubscribing, which we
          // should ignore
          if (!cleanedUp) {
            return listeners.onFormManagerEvent(event);
          }
        },
        listeners.onSubscribe,
      )
      .then((fn) => (unsubscribe = fn))
      .catch(ignorePromiseCancellationException);

    return () => {
      cleanedUp = true;
      subscriptionPromise.cancel(
        `Clean up 'useEffect' subscription to '${absolutePath.toString()}'.`,
      );
      void unsubscribe?.().then(() => listeners.onUnsubscribe?.());
    };
  }, [absolutePath, enabled, formManager, listeners]);
}
